import type { BriefTaskStream } from '../../hooks/useTeamTodaySurface';

/*
 * Brief component library (Task 8, briefing-standardization phase 2), pruned to what the
 * redesigned v2 BriefSurface / ActionsSurface / TeamStandupSurface still consume. (The
 * pre-redesign mockup primitives — Standup, StatTile, WeatherTile, DiffstatRow,
 * BranchDivergenceDetail, STREAM_TONE — were deleted with TaskList/DayAgenda/BandLayout in
 * the redesign port; LeanMeter / ProgressCard / PrioPanel followed in the 2026-07-26
 * mock-alignment pass when the Team-pulse band and the masthead prio rows were retired. The
 * frozen legacy TeamTodaySurface.tsx keeps its own verbatim copies of the pulse markup and
 * is never imported from here.)
 */

const AV_COLORS = ['blue', 'sage', 'sand', 'lilac', 'clay', 'teal'] as const;

function hashIndex(s: string, mod: number): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % mod;
}

function avColor(name: string): string {
  return AV_COLORS[hashIndex(name || '?', AV_COLORS.length)];
}

function initials(name: string): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Pinned stream → chip-label map (spec-mandated; keep in sync with BriefTaskStream).
export const STREAM_LABELS: Record<BriefTaskStream, string> = {
  conflict: 'conflict', overlap: 'overlap', meeting_prep: 'meeting prep',
  linear: 'Linear', notion: 'Notion', github_review: 'review request',
  invite: 'invite', jira: 'Jira', focus: 'focus',
};

// Stream → the single contextual action verb the mockup shows on each task row's right side.
export const STREAM_ACTION: Record<BriefTaskStream, string> = {
  conflict: 'Resolve', overlap: 'Open', meeting_prep: 'Open',
  linear: 'Open', notion: 'Open', github_review: 'Review',
  invite: 'Respond', jira: 'Open', focus: 'Open',
};

// (RelevanceChip — the #B2 percentage pill — was removed 2026-07-27: Teddy reversed the call,
// "the percentage tags do not make sense". Scores still arrive on the payload; nothing renders them.)

/** Square avatar chip — initials on a name-hashed color. Extracted from the repeated
 * `av sq s{size} {color}` span pattern used throughout TeamTodaySurface.tsx. */
export function Avatar({ name, size }: { name: string; size: 16 | 20 | 24 }) {
  return <span className={`av sq s${size} ${avColor(name)}`}>{initials(name)}</span>;
}

// WMO weather-code → icon category (Open-Meteo/WMO codes: 0 clear · 1-2 partly · 3 overcast ·
// 45/48 fog · 51-67 drizzle/rain · 71-77 snow · 80-82 rain showers · 85-86 snow showers · 95-99
// thunderstorm). Rain/snow showers fold into rain/snow (visually identical glyphs).
export type WxCat = 'clear' | 'partly' | 'cloudy' | 'fog' | 'rain' | 'snow' | 'storm';
export function wmoCategory(code?: number | null): WxCat | null {
  if (code == null) return null;
  if (code === 0) return 'clear';
  if (code === 1 || code === 2) return 'partly';
  if (code === 3) return 'cloudy';
  if (code === 45 || code === 48) return 'fog';
  if (code >= 51 && code <= 67) return 'rain';
  if (code >= 71 && code <= 77) return 'snow';
  if (code >= 80 && code <= 82) return 'rain';
  if (code >= 85 && code <= 86) return 'snow';
  if (code >= 95 && code <= 99) return 'storm';
  return 'cloudy';
}
